import type { TOC } from '@ember/component/template-only';
import { t } from 'ember-intl';
import { on } from '@ember/modifier';
import Button from 'tio-ui/components/button';
import { PhCreditCard } from 'ember-phosphor-icons';
import CardBalance from '../card-balance';

const noop = () => {};

interface CourseCashBodySignature {
  Args: {
    institutionName?: string;
    availableBalance?: string;
    useByDate?: string;
    /** Card balance in DOLLARS (the shell converts the model's cents value). */
    cardBalance: number;
    last4?: string;
    /** Routes to the existing CourseCash card-details flow (wired in PR#2). */
    onViewCardDetails?: () => void;
  };
  Element: HTMLDivElement;
}

/**
 * Body of the "Use CourseCash" task: explanatory copy, the authorized/balance/use-by
 * detail rows, the card visual, and a "View Card Details" CTA that opens the existing
 * CourseCash display flow (`tas/course-cash/stripe-card-display`).
 */
const CourseCashBody: TOC<CourseCashBodySignature> = <template>
  <div class="tio-col gap-4" ...attributes>
    <p class="tio-text-sm-normal tio-fg-secondary">
      {{t "tuition_assistance.applications.manage.tasks.use_course_cash_body"}}
    </p>

    <dl class="tio-col gap-1">
      <div class="tio-row flex-wrap items-baseline gap-2">
        <dt class="tio-text-sm-medium tio-fg-secondary">
          {{t "tuition_assistance.applications.manage.tasks.authorized_at"}}
        </dt>
        <dd class="tio-text-sm-normal tio-fg-primary">
          {{@institutionName}}
          {{#if @institutionName}}
            <span class="tio-text-sm-normal tio-fg-secondary italic">
              {{t "tuition_assistance.applications.manage.tasks.authorized_at_hint"}}
            </span>
          {{/if}}
        </dd>
      </div>
      <div class="tio-row flex-wrap items-baseline gap-2">
        <dt class="tio-text-sm-medium tio-fg-secondary">
          {{t "tuition_assistance.applications.manage.tasks.available_balance"}}
        </dt>
        <dd class="tio-text-sm-normal tio-fg-primary">
          {{@availableBalance}}
          {{#if @availableBalance}}
            <span class="tio-text-sm-normal tio-fg-secondary italic">
              {{t "tuition_assistance.applications.manage.tasks.available_balance_hint"}}
            </span>
          {{/if}}
        </dd>
      </div>
      <div class="tio-row flex-wrap items-baseline gap-2">
        <dt class="tio-text-sm-medium tio-fg-secondary">
          {{t "tuition_assistance.applications.manage.tasks.use_by_label"}}
        </dt>
        <dd class="tio-text-sm-normal tio-fg-primary">
          {{@useByDate}}
          {{#if @useByDate}}
            <span class="tio-text-sm-normal tio-fg-secondary italic">
              {{t "tuition_assistance.applications.manage.tasks.use_by_hint"}}
            </span>
          {{/if}}
        </dd>
      </div>
    </dl>

    <CardBalance @balance={{@cardBalance}} @last4={{@last4}} class="max-w-xs" />

    <div>
      <Button
        @intent="primary"
        {{on "click" (if @onViewCardDetails @onViewCardDetails noop)}}
        data-test-view-card-details
      >
        <PhCreditCard @size={{16}} class="mr-2" />
        {{t "tuition_assistance.applications.manage.tasks.view_card_details"}}
      </Button>
    </div>
  </div>
</template>;

export default CourseCashBody;
